/**
 * Copyright Highway9 Networks Inc.
 */
import React, { useEffect, useState } from "react";
import { Button, Dialog, Typography, Tabs, Tab, useTheme, DialogActions, DialogContent, DialogTitle, TabPanel, a11yProps, ErrorsComponent } from "@h9/ui-lib";
import { text } from "../../components/TextResources";
import useErrorsAndWarnings from "../../hooks/useErrorsAndWarnings";
import { authenticationService, userService } from "../../services";
import { GeneralDetails } from "./GeneralDetails";
import { CPIDetails } from "./CPIDetails";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { selectTimeZone, utilityActions } from "~/store/context/utilitySlice";
import moment from "moment-timezone";
import { encrypt } from "~/helpers/utils";
import { useLoading } from "~/store/context/loadingSlice";
import type { EditProfileUser } from "./GeneralDetails";
import type { CpiDetails, UserInfo } from "~/types/user";

/** Extended user state for EditProfile (includes API fields) */
interface EditProfileStateUser extends EditProfileUser {
  id?: string;
  roles?: string[];
}

const TAB_NAMES = {
  GENERAL: "General",
  CPI: "CPI",
} as const;

type TabValue = (typeof TAB_NAMES)[keyof typeof TAB_NAMES];

interface EditProfileProps {
  state: boolean;
  handleClose: () => void;
}

interface UpdateUserRequestBody {
  id?: string;
  name?: string;
  email?: string;
  roles?: string[];
  authType?: string[];
  cpiDetails?: CpiDetails;
  timezone?: string;
  password?: string;
}

interface ApiError {
  errors?: { message: string }[];
  warnings?: { message: string }[];
}

const EditProfile = (props: EditProfileProps): React.ReactElement => {
  const theme = useTheme();
  const { state, handleClose } = props;

  const [user, setUser] = useState<EditProfileStateUser>({});
  const [cpiDetails, setCpiDetails] = useState<Partial<CpiDetails>>({});
  const [timezone, setTimeZone] = useState<string | undefined>();
  const [value, setValue] = useState<number | TabValue>(TAB_NAMES.GENERAL);

  const { errors, warnings, setErrorsAndWarnings, closeAlert, closeAll} = useErrorsAndWarnings();
  const { setLoading, LoadingComponent } = useLoading();
  const dispatch = useAppDispatch();
  const tz = useAppSelector(selectTimeZone);

  useEffect(() => {
    if (state) {
      getUserInfo();
      setErrorsAndWarnings([], []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const getUserInfo = async (): Promise<void> => {
    try {
      const userInfo = (await userService.getUserInfo()) as UserInfo | null;
      setUser((userInfo ?? {}) as EditProfileStateUser);
      if (userInfo?.cpiDetails) {
        setCpiDetails(userInfo.cpiDetails);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const clearCPIDetails = (): void => {
    setCpiDetails({});
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number | TabValue): void => {
    setValue(newValue);
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const requestBody: UpdateUserRequestBody = {
        id: user.id,
        name: user.name,
        email: user.email,
        roles: user.roles,
        authType: user.authType,
        cpiDetails: Object.keys(cpiDetails).length > 0 ? (cpiDetails as CpiDetails) : undefined,
        timezone,
      };

      if (user.newPassword && user.repeatPassword) {
        if (user.newPassword !== user.repeatPassword) {
          setErrorsAndWarnings([{ message: "Password and repeat password does not match" }], []);
          return;
        }
        const sessionInfo = await authenticationService.getSessionInfo(
          (user.email ?? "").trim()
        );
        if (!sessionInfo) {
          return;
        }
        const encrypted = encrypt(user.newPassword, sessionInfo.authKey);
        if (encrypted) requestBody.password = encrypted;
      }

      await userService.updateUserInfo(requestBody as UserInfo);
      dispatch(utilityActions.setTimeZone(timezone ?? moment.tz.guess()));
      if (timezone) moment.tz.setDefault(timezone);
      handleClose();
    } catch (error) {
      console.log(error);
      const apiError = error as ApiError;
      setErrorsAndWarnings(apiError.errors ?? [], apiError.warnings);
    } finally {
      setLoading(false);
      void getUserInfo();
    }
  };

  useEffect(() => {
    setTimeZone(tz);
  }, [tz]);

  return (
    <Dialog aria-labelledby="customized-dialog-title" open={state} fullWidth>
      <DialogTitle id="customized-dialog-title" onClose={handleClose} style={{ padding: "1rem" }}>
        <Typography variant="h6" style={{ verticalAlign: "top" }}>
          {text.editProfile}
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ padding: "1rem" }}>
        <ErrorsComponent
          errorsData={errors}
          warningsData={warnings}
          closeAll={closeAll}
          closeAlert={closeAlert}
        />

        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          aria-label="full width tabs"
        >
          <Tab label={TAB_NAMES.GENERAL} value={TAB_NAMES.GENERAL} {...a11yProps(TAB_NAMES.GENERAL)} />
          <Tab label={TAB_NAMES.CPI} value={TAB_NAMES.CPI} {...a11yProps(TAB_NAMES.CPI)} />
        </Tabs>

        <TabPanel value={value} index={TAB_NAMES.GENERAL as string} dir={theme.direction}>
          <GeneralDetails
            user={user}
            setUser={setUser}
            timezone={timezone}
            changeTimezone={setTimeZone}
            setErrorsAndWarnings={(errs, warns) =>
              setErrorsAndWarnings(errs as { message: string }[], warns as { message: string }[])
            }
          />
        </TabPanel>
        <TabPanel value={value} index={TAB_NAMES.CPI as string} dir={theme.direction}>
          <CPIDetails
            user={user as UserInfo  }
            cpiDetails={cpiDetails as CpiDetails}
            clearCPIDetails={clearCPIDetails}
          />
        </TabPanel>
      </DialogContent>
      <DialogActions style={{ padding: "1rem" }}>
        <Button variant="contained" autoFocus onClick={handleSubmit} color="primary">
          {text.updateProfile_btn}
        </Button>
      </DialogActions>
      <LoadingComponent />
    </Dialog>
  );
};

export default EditProfile;
